import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { FullPageLoader } from "@/components/fullpage-loader";
import {
  derivePlanFromApiData,
  parseSubscriptionSearchParams,
} from "@/features/auth/subscription/hooks/subscriptionIntentApi.ts";
import { useGoogleTagManager } from "@/features/auth/subscription/hooks/useGoogleTagManager.ts";
import { useSubscriptionIntent } from "@/features/auth/subscription/hooks/useSubscriptionIntentHook.ts";
import { stashFirstPromoterReferralEmail } from "@/features/auth/subscription/utils/firstPromoter";
import {
  isFirstDraft,
  isFullyManaged,
} from "@/features/auth/subscription/utils/planType";
import { usePageTitle } from "@/hooks/usePageTitle";
import { pushDataLayer } from "@/lib/dataLayer";

import type {
  AppliedPromotion,
  CheckoutSummaryCardHandle,
  SubscriptionFormHandle,
} from "./components/SubscriptionType";
import type { SubscriptionFormData } from "./hooks/useSubscriptionForm";

import { CheckoutEmailStep } from "./components/CheckoutEmailStep";
import { CheckoutLayout } from "./components/CheckoutLayout";
import { CheckoutSummaryCard } from "./components/CheckoutSummaryCard";
import SubscriptionForm from "./components/SubscriptionForm";
import {
  couponBadgeLabel,
  formatMoney,
  intervalFromRecurring,
  intervalLabels,
  isDiscountActiveAtNextCharge,
} from "./utils/checkoutPricing";
import { toIsoCurrency } from "./utils/purchaseEvent";

export function SubscriptionPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const submitInFlightRef = useRef(false);
  const summaryRef = useRef<CheckoutSummaryCardHandle | null>(null);
  const [appliedPromotion, setAppliedPromotion] =
    useState<AppliedPromotion | null>(null);
  const formRef = useRef<null | SubscriptionFormHandle>(null);
  const [promoCode, setPromoCode] = useState<null | string>(null);
  const [promotionCodeId, setPromotionCodeId] = useState<null | string>(null);
  const promotionRef = useRef<{
    code: null | string;
    finalAmountCents: null | number;
    id: null | string;
  } | null>(null);

  const search = useSearch({ strict: false }) as {
    promo?: string;
    promo_code?: number | string;
    receipt_email?: string;
  };
  const subscriptionParams = parseSubscriptionSearchParams();
  const [step, setStep] = useState<"email" | "payment">("email");
  const [checkoutLeadId, setCheckoutLeadId] = useState<null | string>(null);
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const showPromo = Number(search.promo) === 1;
  // TanStack parses numeric search values, so a code like "2024" arrives as a number.
  const promoCodeParam =
    search.promo_code == null ? undefined : String(search.promo_code);

  useGoogleTagManager("GTM-5X67HTS");

  useEffect(() => {
    const script = document.createElement("script");

    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    document.head.append(script);

    return () => {
      script.remove();
    };
  }, []);

  const { data: apiData, error, isLoading: loading } = useSubscriptionIntent();

  const derivedPlan = useMemo(() => derivePlanFromApiData(apiData), [apiData]);

  usePageTitle(derivedPlan?.name ?? "Subscription");

  const handleFormSubmit = async (data: SubscriptionFormData) => {
    if (derivedPlan) {
      stashFirstPromoterReferralEmail(data.email);

      try {
        const purchaseData = {
          billingFrequency: derivedPlan.billingCycle,
          companyName: data.companyName || undefined,
          ownerEmail: data.email,
          ownerMobile: data.phoneNumber || undefined,
          ownerName: data.fullName,
          planPrice: `${derivedPlan.currency}${derivedPlan.price}`,
          platformName: data.companyName || undefined,
          productName: derivedPlan.name,
          promoCode: promoCode || undefined,
          purchaseType: "regular_subscription" as const,
          stripeCustomerId: data.stripePaymentMethodId || undefined,
        };

        localStorage.setItem(
          "pendingPurchaseNotification",
          JSON.stringify(purchaseData),
        );
      } catch (error) {
        console.error("Failed to save product purchase data:", error);
      }

      try {
        const successData = {
          billingCycle: derivedPlan.billingCycle,
          couponLabel: appliedPromotion
            ? (couponBadgeLabel(
                appliedPromotion.coupon,
                toIsoCurrency(derivedPlan.currency, "USD"),
              ) ?? undefined)
            : undefined,
          currency: derivedPlan.currency,
          description: derivedPlan.description,
          email: data.email,
          isExistingAccount: !!search.receipt_email,
          planName: derivedPlan.name,
          price: derivedPlan.price,
        };

        localStorage.setItem(
          "subscriptionSuccessData",
          JSON.stringify(successData),
        );
      } catch (error) {
        console.error("Failed to save subscription success data:", error);
      }
    }

    const searchParams = new URLSearchParams(window.location.search);
    const receiptEmail = searchParams.get("receipt_email");
    const resolvedCoupon = promotionRef.current?.code ?? promoCode ?? undefined;
    const chargedCents =
      promotionRef.current?.finalAmountCents ?? data.amount ?? undefined;
    const trackingParams = {
      ...(data.subscriptionId ? { sub_id: data.subscriptionId } : {}),
      ...(typeof chargedCents === "number"
        ? { value: (chargedCents / 100).toFixed(2) }
        : {}),
    };

    if (!receiptEmail) {
      navigate({
        search: {
          checkEmail: "true",
          ...(derivedPlan
            ? {
                billingCycle: derivedPlan.billingCycle,
                currency: derivedPlan.currency,
                origin: "direct-purchase",
                planName: derivedPlan.name,
                price: derivedPlan.price,
              }
            : {}),
          ...(data.invoiceId ? { inv_id: data.invoiceId } : {}),
          ...(resolvedCoupon ? { coupon: resolvedCoupon } : {}),
          ...trackingParams,
        },
        to: "/subscription-success",
      });
    } else if (derivedPlan) {
      const firstDraftPlan = isFirstDraft(derivedPlan.name);
      const fullyManagedPlan =
        !firstDraftPlan && isFullyManaged("direct-purchase", derivedPlan.name);

      if (fullyManagedPlan || firstDraftPlan) {
        navigate({
          search: {
            billingCycle: derivedPlan.billingCycle,
            currency: derivedPlan.currency,
            origin: "direct-purchase",
            planName: derivedPlan.name,
            price: derivedPlan.price,
            ...(data.invoiceId ? { inv_id: data.invoiceId } : {}),
            ...(resolvedCoupon ? { coupon: resolvedCoupon } : {}),
            ...trackingParams,
          },
          to: "/subscription-success",
        });
      } else {
        navigate({
          search: {
            billingCycle: derivedPlan.billingCycle,
            checkEmail: "true",
            currency: derivedPlan.currency,
            origin: "direct-purchase",
            planName: derivedPlan.name,
            price: derivedPlan.price,
            ...(data.invoiceId ? { inv_id: data.invoiceId } : {}),
            ...(resolvedCoupon ? { coupon: resolvedCoupon } : {}),
            ...trackingParams,
          },
          to: "/subscription-success",
        });
      }
    } else {
      navigate({ to: "/subscription-success" });
    }
  };

  const handlePromoApplied = (promotion: AppliedPromotion) => {
    setAppliedPromotion(promotion);
    setPromoCode(promotion.code);
    setPromotionCodeId(promotion.promotionCodeId);
    promotionRef.current = {
      code: promotion.code,
      finalAmountCents: promotion.finalAmountCents,
      id: promotion.promotionCodeId,
    };
  };

  const handleSubmitPress = async () => {
    if (submitInFlightRef.current || isApplyingPromo) return;
    submitInFlightRef.current = true;
    try {
      pushDataLayer({
        event: "subscribe",
        page_url: "subscription",
        section: "checkout_submit",
      });

      const promoReady =
        (await summaryRef.current?.applyPendingPromo()) ?? true;

      if (!promoReady) return;
      // Give React a tick so the applied promotion reaches SubscriptionForm props before submitting.
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!formRef.current?.submit) return;
      setIsSubmitting(true);
      try {
        await formRef.current.submit();
      } finally {
        setIsSubmitting(false);
      }
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const interval = intervalFromRecurring(apiData?.price?.recurring);
  const labels = intervalLabels(interval);
  const currencyCode = toIsoCurrency(derivedPlan?.currency, "USD");
  const dueTodayMajor = appliedPromotion
    ? appliedPromotion.finalAmountCents / 100
    : Number(derivedPlan?.price ?? 0);
  const dueToday = formatMoney(dueTodayMajor, currencyCode);
  const isDueTodayRecurring =
    !appliedPromotion ||
    isDiscountActiveAtNextCharge(appliedPromotion.coupon, interval);
  const submitPrice = isDueTodayRecurring
    ? `${dueToday}/${labels.shortUnit}`
    : dueToday;

  return (
    <CheckoutLayout>
      {loading ? (
        <FullPageLoader />
      ) : error || !derivedPlan ? (
        // Error State with CTA
        <div className="bg-bg-primary rounded-2xl shadow-sm border border-border-secondary p-6 text-center">
          <p className="text-red-600 font-semibold mb-4">
            Something went wrong.
          </p>
          <p className="text-text-tertiary mb-6">
            The payment link is not working. Please contact our support team.
          </p>
          <a
            className="inline-block px-6 py-3 rounded-md bg-fg-brand-tertiary text-white font-semibold hover:opacity-90"
            href="https://nlc.com/book-a-call"
            rel="noreferrer"
            target="_blank"
          >
            Connect with NLC
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_484px]">
          <div>
            {step === "email" && (
              <CheckoutEmailStep
                checkoutLeadId={checkoutLeadId}
                couponCode={showPromo ? promoCodeParam : undefined}
                defaultEmail={
                  checkoutEmail ||
                  apiData?.prefill?.email ||
                  search.receipt_email ||
                  ""
                }
                onContinue={({ email, id }) => {
                  setCheckoutLeadId(id);
                  setCheckoutEmail(email);
                  setStep("payment");
                }}
                ownAccountEmail={search.receipt_email}
                productId={subscriptionParams.product_id}
                stripePriceId={subscriptionParams.stripe_price_id}
              />
            )}
            <div className={step === "email" ? "hidden" : undefined}>
              <SubscriptionForm
                chargeSummary={`You'll be charged ${dueToday} today. ${labels.renews} until you cancel.`}
                checkoutLeadId={checkoutLeadId}
                initialEmail={checkoutEmail}
                isSubmitDisabled={isApplyingPromo}
                isSubmitting={isSubmitting}
                onChangeEmail={() => setStep("email")}
                onSubmit={handleFormSubmit}
                onSubmitPress={handleSubmitPress}
                onSubmittingChange={setIsSubmitting}
                promotionCode={promoCode}
                promotionCodeId={promotionCodeId}
                promotionRef={promotionRef}
                ref={formRef}
                submitLabel={`Start subscription — ${submitPrice}`}
                subscriptionApiData={apiData || undefined}
              />
            </div>
          </div>
          <div className="lg:sticky lg:top-8 lg:self-start">
            <CheckoutSummaryCard
              initialCoupon={promoCodeParam}
              interval={interval}
              onApplyingChange={setIsApplyingPromo}
              onPromoApplied={handlePromoApplied}
              plan={derivedPlan}
              ref={summaryRef}
              showPromo={showPromo}
            />
          </div>
        </div>
      )}
    </CheckoutLayout>
  );
}
